<script lang="ts">
    import {
        getModalStore,
        type ModalStore,
        ProgressRadial,
        type ToastStore,
        getToastStore,
    } from "@skeletonlabs/skeleton";
    import { fade } from "svelte/transition";
    import { onMount } from "svelte";

    import type { search_qa_result } from "$lib/types/service_types";

    import BlackX from "$lib/assets/BlackX.svg";
    import SearchGlass from "$lib/assets/SearchGlass.svg";

    const modalStore: ModalStore = getModalStore();
    const toastStore: ToastStore = getToastStore();

    let mode: "search" | "confirm" = "search";

    let search_resp: Promise<void>;
    let search_data: search_qa_result[] = [];
    let search_loading: boolean = false;
    let selected_id: string;

    let qa_search_input: string = "";

    function search(input: string): void {
        if (!input) return;

        selected_id = "";
        search_loading = true;

        search_resp = fetch("/api/qa_pairs/search", {
            method: "POST",
            body: JSON.stringify({
                input: input,
            }),
        })
            .then((resp) => {
                if (resp.ok) {
                    resp.json().then((res) => (search_data = res.results));
                } else {
                    search_data = [];
                    resp.json()
                        .then((res) => {
                            toastStore.trigger({
                                message: "✗ " + res.message,
                                background: "bg-zinc-800 text-red-400 text-center",
                                hideDismiss: true,
                                timeout: 8000,
                            });
                        })
                        .catch(() => {
                            toastStore.trigger({
                                message:
                                    "✗ Something went wrong. Please try again or contact us if this error persists.",
                                background: "bg-zinc-800 text-red-400 text-center",
                                hideDismiss: true,
                                timeout: 8000,
                            });
                        });
                }
            })
            .finally(() => (search_loading = false));
    }

    function submit(e: Event): void {
        e.preventDefault();
        $modalStore[0].response!({ output: search_data.find((ele) => ele.external_id == selected_id)!.answer });

        fetch("/api/qa_pairs/usage", {
            method: "POST",
            body: JSON.stringify({
                external_id: selected_id,
                application_id: $modalStore[0].meta.application_id,
            }),
        });

        modalStore.clear();
    }

    onMount(() => {
        search($modalStore[0].meta.input);
    });
</script>

<div class="p-8 rounded-lg bg-white w-[600px]">
    {#if mode == "search"}
        <div class="flex items-center">
            <p class="font-bold">Insert From Q&A Library</p>
            <button
                class="ml-auto rounded-full hover:bg-stone-100 transition duration-300 p-1 focus:ring-0 focus:outline-0"
                on:click={() => modalStore.clear()}
            >
                <img src={BlackX} alt="Exit" />
            </button>
        </div>

        <div class="flex rounded-full bg-white border border-zinc-400 h-10 flex items-center p-2 w-fit mt-3">
            <input
                type="text"
                class="text-sm h-6 border-transparent focus:border-transparent focus:ring-0 focus:outline-0"
                placeholder="Search"
                bind:value={qa_search_input}
                on:keydown={(e) => {
                    if (e.key === "Enter" && !search_loading) {
                        search(qa_search_input);
                    }
                }}
            />

            <div class="relative size-6">
                {#if search_loading}
                    <ProgressRadial
                        transitionIn={fade}
                        transitionInParams={{ duration: 150, delay: 150 }}
                        transitionOut={fade}
                        transitionOutParams={{ duration: 150 }}
                        stroke={3}
                        width="w-6"
                        meter="stroke-black"
                        track="stroke-secondary-600"
                        class="absolute top-0 left-0"
                        value={undefined}
                    />
                {:else}
                    <div
                        in:fade={{ duration: 150, delay: 150 }}
                        out:fade={{ duration: 150 }}
                        class="absolute top-0 left-0 p-1"
                    >
                        <img src={SearchGlass} alt="Search" />
                    </div>
                {/if}
            </div>
        </div>

        <div class="max-h-[500px] overflow-y-auto grid grid-cols-1 gap-y-2 my-5">
            {#await search_resp}
                <p class="my-10 text-center text-sm text-neutral-500">Loading...</p>
            {:then}
                {#if search_data.length}
                    {#each search_data as result}
                        <button
                            class="rounded-lg border border-stone-200 flex items-center focus:outline-0 px-4 py-4 transition duration-300"
                            class:bg-stone-100={selected_id != result.external_id}
                            class:hover:bg-stone-200={selected_id != result.external_id}
                            class:bg-stone-300={selected_id == result.external_id}
                            on:click={() => (selected_id = result.external_id)}
                        >
                            <div class="flex flex-col">
                                <span class="flex items-center mb-2">
                                    <p class="text-xs w-full text-left font-bold">{result.question}</p>
                                    <div class="rounded-full size-4 border border-black">
                                        {#if selected_id == result.external_id}
                                            <div transition:fade={{ duration: 300 }} class="size-full bg-black rounded-full" />
                                        {/if}
                                    </div>
                                </span>
                                <p class="text-xs w-full text-left">{result.answer}</p>
                            </div>
                        </button>
                    {/each}
                {:else}
                    <p class="my-10 text-center text-sm text-neutral-500">No Results</p>
                {/if}
            {/await}
        </div>

        <button
            class="px-4 py-2 bg-zinc-800 rounded-full disabled:bg-secondary-600 text-white text-sm transition duration-300"
            disabled={!selected_id}
            on:click={() => (mode = "confirm")}
        >
            Insert Selected
        </button>
    {:else if mode == "confirm"}
        <div class="flex items-center">
            <p class="font-bold">Replace Answer?</p>
            <button
                class="ml-auto rounded-full hover:bg-stone-100 transition duration-300 p-1"
                on:click={() => modalStore.clear()}
            >
                <img src={BlackX} alt="Exit" />
            </button>
        </div>

        <form on:submit={submit}>
            <p class="mt-8 text-sm">
                By continuing, the question's current response will be overwritten by the chosen answer. You'll still
                have to save the new response afterwards.
            </p>
            <div class="flex mt-10">
                <button
                    type="submit"
                    class="rounded-full bg-zinc-800 px-4 py-2 text-white hover:shadow-md transition duration-300 text-sm"
                >
                    Replace
                </button>
                <button
                    type="button"
                    class="rounded-full border border-black text-sm ml-3 px-4 py-2 hover:shadow-md transition duration-300"
                    on:click={() => modalStore.clear()}
                >
                    Cancel
                </button>
            </div>
        </form>
    {/if}
</div>
